/**
 * FSD Part 2 — Finance (docs §19161). C17: one section per FSD feature
 * specification, in this order — 2.2.1 (FIN-F01, ledger), 2.2.2 (FIN-F02,
 * payables), 2.2.3 (FIN-F03, receivables), 2.2.4 (FIN-F04, fixed assets),
 * 2.2.5 (FIN-F06, budgets), 2.2.6 (FIN-F08, cost accounting), 2.2.7
 * (FIN-F07, consolidation), 2.2.8 (FIN-F09, close) — then the Part 2
 * requirement catalogue (2.1). Section titles come from `nav.fsd.*`.
 *
 * The 2.1 section holds tax, expense and intercompany screens that have no
 * feature specification of their own but answer a requirement in the
 * catalogue (FSD A.5: the requirement governs). Each carries the requirement
 * id it answers as `code`; none is flagged extraneous.
 *
 * Tax packs, tax jurisdictions and tax codes are NOT here: they are
 * Enterprise configuration and live in `p01-enterprise.ts`. The FX screens
 * live there too. Interco reconciliation pairs ARE here (2.1, FIN-FR-030):
 * R0 moved them back from Part 1, which holds the FSD 1.2.3 legal-entity
 * intercompany rules instead.
 *
 * Item ids are stable and must stay so: favourites and recents in
 * localStorage key off them. Sections moved; items did not change.
 */
import type { NavGroup } from '../types'
import { PERMISSIONS } from '@/lib/permissions'
import { AP_PERMISSIONS } from '@/modules/ap/permissions'
import { AR_COLLECTIONS_PERMISSIONS } from '@/modules/ar/collections/permissions'
import { AR_CREDIT_PERMISSIONS } from '@/modules/ar/credit-control/permissions'
import { AR_PERMISSIONS } from '@/modules/ar/permissions'
import { AR_WRITE_OFF_PERMISSIONS } from '@/modules/ar/write-offs/permissions'
import { ASSETS_PERMISSIONS } from '@/modules/assets/permissions'
import { BUDGET_PERMISSIONS } from '@/modules/budget/permissions'
import { BUDGET_CONTROL_LEVEL_PERMISSIONS } from '@/modules/budget/control-levels/permissions'
import { BUDGET_CYCLE_PERMISSIONS } from '@/modules/budget/cycles/permissions'
import { BUDGET_SUPPLEMENTARY_PERMISSIONS } from '@/modules/budget/supplementary/permissions'
import { BUDGET_TRANSFER_PERMISSIONS } from '@/modules/budget/transfers/permissions'
import { CLOSE_PERMISSIONS } from '@/modules/close/permissions'
import { CONSOL_ADJUSTMENT_PERMISSIONS } from '@/modules/consol/adjustments/routes'
import { CONSOL_PERMISSIONS } from '@/modules/consol/permissions'
import { COST_PERMISSIONS } from '@/modules/cost/permissions'
import { EXPENSE_PERMISSIONS } from '@/modules/expense/permissions'
import { INTERCO_PERMISSIONS } from '@/modules/interco/permissions'
import { GL_PERMISSIONS } from '@/modules/gl/permissions'
import { TAX_SPINE_PERMISSIONS } from '@/modules/tax/permissions'
import { FX_EXPOSURE_PERMISSIONS } from '@/modules/fx/exposure/permissions'
import { TP_PERMISSIONS } from '@/modules/interco/transfer-pricing/permissions'
import { LEASE_PERMISSIONS } from '@/modules/lease/permissions'
import { LIABILITY_PERMISSIONS } from '@/modules/liability/permissions'
import { REVPOLICY_PERMISSIONS } from '@/modules/revpolicy/permissions'

export const P02_FINANCE_GROUPS: NavGroup[] = [
  {
    id: 's-2.2.1',
    kind: 'feature',
    fsd: '2.2.1',
    code: 'FIN-F01',
    labelKey: 'nav.fsd.s2_2_1.short',
    fullTitleKey: 'nav.fsd.s2_2_1.full',
    items: [
      {
        id: 'gl-journal-entries',
        labelKey: 'nav.items.glJournalEntries.label',
        to: '/gl/journal-entries',
        icon: 'inbox',
        permission: GL_PERMISSIONS.journalRead,
        keywordsKey: 'nav.items.glJournalEntries.keywords',
        descriptionKey: 'nav.items.glJournalEntries.description',
      },
      // FT6-U14 (FSD 2.2.1.11): the journal register, manual journal analysis and the
      // cash-flow statement. Labels are module-owned (gl.ft6.routeTitles.*), like the
      // governance items below; each refines the section's own `fsd`.
      {
        id: 'gl-journal-register',
        labelKey: 'gl.ft6.routeTitles.journalRegister',
        to: '/gl/reports/journal-register',
        icon: 'history',
        permission: GL_PERMISSIONS.journalRead,
        fsd: '2.2.1.11',
      },
      {
        id: 'gl-recurring-templates',
        // FT6-U14: renamed "Journal Templates" (gl.ft6.routeTitles.journalTemplates); the
        // old nav.items key stays for keywords and description.
        labelKey: 'gl.ft6.routeTitles.journalTemplates',
        to: '/gl/recurring-templates',
        icon: 'clock',
        permission: GL_PERMISSIONS.recurringRead,
        keywordsKey: 'nav.items.glRecurringTemplates.keywords',
        descriptionKey: 'nav.items.glRecurringTemplates.description',
      },
      {
        id: 'gl-trial-balance',
        labelKey: 'nav.items.glTrialBalance.label',
        to: '/gl/reports/trial-balance',
        icon: 'dashboard',
        permission: GL_PERMISSIONS.reportRead,
        keywordsKey: 'nav.items.glTrialBalance.keywords',
        descriptionKey: 'nav.items.glTrialBalance.description',
      },
      {
        id: 'gl-ledger',
        labelKey: 'nav.items.glLedger.label',
        to: '/gl/reports/ledger',
        icon: 'history',
        permission: GL_PERMISSIONS.reportRead,
        keywordsKey: 'nav.items.glLedger.keywords',
        descriptionKey: 'nav.items.glLedger.description',
      },
      {
        id: 'gl-income-statement',
        labelKey: 'nav.items.glIncomeStatement.label',
        to: '/gl/reports/income-statement',
        icon: 'dashboard',
        permission: GL_PERMISSIONS.reportRead,
        keywordsKey: 'nav.items.glIncomeStatement.keywords',
        descriptionKey: 'nav.items.glIncomeStatement.description',
      },
      {
        id: 'gl-balance-sheet',
        labelKey: 'nav.items.glBalanceSheet.label',
        to: '/gl/reports/balance-sheet',
        icon: 'building',
        permission: GL_PERMISSIONS.reportRead,
        keywordsKey: 'nav.items.glBalanceSheet.keywords',
        descriptionKey: 'nav.items.glBalanceSheet.description',
      },
      {
        id: 'gl-cash-flow',
        labelKey: 'gl.ft6.routeTitles.cashFlow',
        to: '/gl/reports/cash-flow',
        icon: 'dashboard',
        permission: GL_PERMISSIONS.reportRead,
        fsd: '2.2.1.11',
      },
      {
        id: 'gl-manual-journals',
        labelKey: 'gl.ft6.routeTitles.manualJournals',
        to: '/gl/reports/manual-journals',
        icon: 'dashboard',
        permission: GL_PERMISSIONS.reportRead,
        fsd: '2.2.1.11',
      },
      {
        id: 'gl-suspense',
        labelKey: 'gl.routeTitles.suspense',
        to: '/gl/suspense',
        icon: 'alert',
        permission: GL_PERMISSIONS.suspenseRead,
      },
      {
        id: 'gl-accruals',
        labelKey: 'gl.routeTitles.accruals',
        to: '/gl/accruals',
        icon: 'clock',
        permission: GL_PERMISSIONS.accrualRead,
      },
      // C3 (design C3_FINANCE_DEPTH.md §5.9, §13 P9): posting governance —
      // journal types, per-account dimension policy, suspense, accruals and the
      // completeness board. labelKey reuses gl.routeTitles.* (module-owned)
      // rather than a new nav.items.* entry.
      {
        id: 'gl-journal-types',
        labelKey: 'gl.routeTitles.journalTypes',
        to: '/gl/journal-types',
        icon: 'settings',
        permission: GL_PERMISSIONS.journalTypeRead,
      },
      {
        id: 'gl-dimension-policies',
        labelKey: 'gl.routeTitles.dimensionPolicies',
        to: '/gl/dimension-policies',
        icon: 'sitemap',
        // Reads the chart of accounts to pick the account whose policy is being
        // set (§6.6: GET /accounts/:id/dimension-policies -> gl.account.read).
        permission: GL_PERMISSIONS.accountRead,
      },
      {
        id: 'gl-posting-assertions',
        labelKey: 'gl.routeTitles.assertions',
        to: '/gl/posting-assertions',
        icon: 'shieldCheck',
        permission: GL_PERMISSIONS.assertionRead,
      },
    ],
  },
  // Labels point at each module's OWN title key rather than a `nav.items.*.label`
  // of their own. A sidebar entry and the screen it opens naming the same page two
  // different ways is the kind of drift nobody files a bug for, and one key cannot
  // drift from itself. Only the palette descriptions are written here, because no
  // screen has anywhere to put them.
  {
    id: 's-2.2.2',
    kind: 'feature',
    fsd: '2.2.2',
    code: 'FIN-F02',
    labelKey: 'nav.fsd.s2_2_2.short',
    fullTitleKey: 'nav.fsd.s2_2_2.full',
    items: [
      {
        id: 'ap-invoices',
        labelKey: 'routes.titles.apInvoices',
        to: '/ap/invoices',
        icon: 'inbox',
        permission: AP_PERMISSIONS.invoiceRead,
        descriptionKey: 'nav.items.apInvoices.description',
      },

      /* Credit and debit notes are documents of the same family as the invoice
       * above them and belong beside it: a note is not reached from an invoice
       * (it usually adjusts a different one), so the invoice list is not its
       * door. Both read on `ap.invoice.read`, which is the code their routes
       * declare — the note is the same sub-ledger seen from the other side.
       *
       * Vendor bank-detail changes is the dual-approval queue (§8.3). It reads on
       * `ap.vendor.bank.read` rather than `ap.vendor.read`: the queue shows bank
       * fields, and the whole point of the separate code is that seeing a vendor
       * and seeing its bank account are different entitlements. Approving is
       * gated on the screen's own action and carries step-up. */
      {
        id: 'ap-credit-notes',
        labelKey: 'routes.titles.apCreditNotes',
        to: '/ap/credit-notes',
        icon: 'refresh',
        permission: AP_PERMISSIONS.invoiceRead,
      },
      {
        id: 'ap-debit-notes',
        labelKey: 'routes.titles.apDebitNotes',
        to: '/ap/debit-notes',
        icon: 'refresh',
        permission: AP_PERMISSIONS.invoiceRead,
      },
      {
        id: 'ap-payment-runs',
        labelKey: 'ap.runs.title',
        to: '/ap/payment-runs',
        icon: 'download',
        permission: AP_PERMISSIONS.paymentRead,
        descriptionKey: 'nav.items.apPaymentRuns.description',
      },
      {
        id: 'ap-payment-exceptions',
        labelKey: 'ap.routeTitles.paymentExceptions',
        to: '/ap/payment-exceptions',
        icon: 'ban',
        permission: AP_PERMISSIONS.paymentRead,
      },
      // C3 (design §5.9, §13 P9): the signal inbox and the payment exception
      // matrix. labelKey reuses ap.routeTitles.*.
      {
        id: 'ap-signals',
        labelKey: 'ap.routeTitles.signals',
        to: '/ap/signals',
        icon: 'alert',
        permission: AP_PERMISSIONS.signalRead,
      },
      {
        id: 'ap-duplicate-payments',
        labelKey: 'ap.routeTitles.duplicatePayments',
        to: '/ap/reports/duplicate-payments',
        icon: 'alert',
        permission: AP_PERMISSIONS.reportRead,
      },
      {
        id: 'ap-bank-changes',
        labelKey: 'ap.bankChanges.title',
        to: '/ap/bank-changes',
        icon: 'shieldCheck',
        permission: AP_PERMISSIONS.bankRead,
      },
      {
        id: 'ap-aging',
        labelKey: 'ap.aging.title',
        to: '/ap/reports/aging',
        icon: 'clock',
        permission: AP_PERMISSIONS.reportRead,
        descriptionKey: 'nav.items.apAging.description',
      },
      /* FT10-U16 (FSD 2.2.2, FIN-F02 reports): the supplier statement, the statement
       * reconciliation, the withholding register with remittances, and duplicate-payment
       * analysis. Three read on `ap.report.read` (the report endpoints'), the withholding
       * register on `ap.wht.read`; acting on a remittance or recording a statement needs the
       * action's own permission, checked on the screen. */
      {
        id: 'ap-supplier-statement',
        labelKey: 'ap.routeTitles.supplierStatement',
        to: '/ap/reports/supplier-statement',
        icon: 'inbox',
        permission: AP_PERMISSIONS.reportRead,
      },
      {
        id: 'ap-statement-reconciliation',
        labelKey: 'ap.routeTitles.statementReconciliation',
        to: '/ap/reports/statement-reconciliation',
        icon: 'scale',
        permission: AP_PERMISSIONS.reportRead,
      },
      {
        id: 'ap-withholding',
        labelKey: 'ap.routeTitles.withholding',
        to: '/ap/reports/withholding',
        icon: 'landmark',
        permission: AP_PERMISSIONS.whtRead,
        code: 'FIN-FR-006',
      },
      {
        id: 'ap-vendors',
        labelKey: 'ap.vendors.title',
        to: '/ap/vendors',
        icon: 'users',
        permission: AP_PERMISSIONS.vendorRead,
        descriptionKey: 'nav.items.apVendors.description',
      },
      // R0 W1 (FIN-FR-006): the AP settings screen. `ap.settings.manage` guards
      // both read and write on the server, so there is no read-only variant.
      {
        id: 'ap-settings',
        labelKey: 'nav.items.apSettings.label',
        to: '/ap/settings',
        icon: 'settings',
        permission: AP_PERMISSIONS.settingsManage,
        keywordsKey: 'nav.items.apSettings.keywords',
        descriptionKey: 'nav.items.apSettings.description',
      },
    ],
  },
  {
    id: 's-2.2.3',
    kind: 'feature',
    fsd: '2.2.3',
    code: 'FIN-F03',
    labelKey: 'nav.fsd.s2_2_3.short',
    fullTitleKey: 'nav.fsd.s2_2_3.full',
    items: [
      {
        id: 'ar-customers',
        labelKey: 'ar.routeTitles.arCustomers',
        to: '/ar/customers',
        icon: 'users',
        permission: AR_PERMISSIONS.customerRead,
        descriptionKey: 'nav.items.arCustomers.description',
      },
      {
        id: 'ar-invoices',
        labelKey: 'ar.routeTitles.arInvoices',
        to: '/ar/invoices',
        icon: 'inbox',
        permission: AR_PERMISSIONS.invoiceRead,
        descriptionKey: 'nav.items.arInvoices.description',
      },
      {
        id: 'ar-credit-notes',
        labelKey: 'ar.routeTitles.arCreditNotes',
        to: '/ar/credit-notes',
        icon: 'refresh',
        permission: AR_PERMISSIONS.creditNoteRead,
      },
      {
        id: 'ar-receipts',
        labelKey: 'ar.routeTitles.arReceipts',
        to: '/ar/receipts',
        icon: 'check',
        permission: AR_PERMISSIONS.receiptRead,
        descriptionKey: 'nav.items.arReceipts.description',
      },
      {
        id: 'ar-unallocated-receipts',
        labelKey: 'ar.routeTitles.unallocatedReceipts',
        to: '/ar/reports/unallocated-receipts',
        icon: 'inbox',
        permission: AR_PERMISSIONS.receiptRead,
      },
      // FT8-U17 (FSD 2.2.3): the credit-control screen (Check Credit, Place on Credit Hold,
      // credit limit changes, Credit Exposure Report) and the dunning response report. Both
      // are 2.2.3's own, so neither carries a separate `code`.
      {
        id: 'ar-credit-control',
        labelKey: 'ar.routeTitles.creditControl',
        to: '/ar/credit-control',
        icon: 'shieldCheck',
        permission: AR_CREDIT_PERMISSIONS.read,
      },
      {
        id: 'ar-dunning',
        labelKey: 'ar.routeTitles.arDunning',
        to: '/ar/dunning',
        icon: 'mail',
        permission: AR_PERMISSIONS.dunningRead,
        descriptionKey: 'nav.items.arDunning.description',
      },
      {
        id: 'ar-dunning-effectiveness',
        labelKey: 'ar.routeTitles.dunningEffectiveness',
        to: '/ar/reports/dunning-effectiveness',
        icon: 'download',
        permission: AR_PERMISSIONS.dunningRead,
      },
      // FT12-U18 (FSD 2.2.3.8–2.2.3.10): the collections worklist with promises to pay, the
      // collection effectiveness index and the unallocated receipts report. All three are 2.2.3's
      // own, so none carries a separate `code`.
      {
        id: 'ar-collections',
        labelKey: 'ar.routeTitles.collections',
        to: '/ar/collections',
        icon: 'users',
        permission: AR_COLLECTIONS_PERMISSIONS.read,
      },
      /* FT14-U19 (FSD 2.2.3 V06): write-offs, proposed, approved and posted by three different
       * people, and the value bands that decide how many approvals one needs. The document routes
       * (`/ar/write-offs/new`, `/ar/write-offs/:id`) sit under the first item's path. */
      {
        id: 'ar-write-offs',
        labelKey: 'ar.routeTitles.writeOffs',
        to: '/ar/write-offs',
        icon: 'scale',
        permission: AR_WRITE_OFF_PERMISSIONS.read,
        code: 'FIN-F03-V06',
      },
      {
        id: 'ar-write-off-settings',
        labelKey: 'ar.routeTitles.writeOffSettings',
        to: '/ar/write-off-settings',
        icon: 'settings',
        permission: AR_WRITE_OFF_PERMISSIONS.read,
        code: 'FIN-F03-V06',
      },
      {
        id: 'ar-aging',
        labelKey: 'ar.routeTitles.arAging',
        to: '/ar/reports/aging',
        icon: 'clock',
        permission: AR_PERMISSIONS.reportRead,
        descriptionKey: 'nav.items.arAging.description',
      },

      /* Both read on `ar.report.read`, beside the aging that shares it. */
      {
        id: 'ar-dso',
        labelKey: 'ar.routeTitles.arDso',
        to: '/ar/reports/dso',
        icon: 'dashboard',
        permission: AR_PERMISSIONS.reportRead,
      },
      {
        id: 'ar-cei',
        labelKey: 'ar.routeTitles.cei',
        to: '/ar/reports/cei',
        icon: 'dashboard',
        permission: AR_PERMISSIONS.reportRead,
      },
      {
        id: 'ar-statement',
        labelKey: 'ar.routeTitles.arStatement',
        to: '/ar/reports/statement',
        icon: 'mail',
        permission: AR_PERMISSIONS.reportRead,
      },
      // C3 (design §5.9, §13 P9): expected credit loss. labelKey reuses
      // ar.routeTitles.*.
      {
        id: 'ar-ecl-matrices',
        labelKey: 'ar.routeTitles.eclMatrices',
        to: '/ar/ecl/matrices',
        icon: 'scale',
        permission: AR_PERMISSIONS.eclRead,
      },
      {
        id: 'ar-ecl-runs',
        labelKey: 'ar.routeTitles.eclRuns',
        to: '/ar/ecl/runs',
        icon: 'refresh',
        permission: AR_PERMISSIONS.eclRead,
      },
      {
        id: 'ar-ecl-assessments',
        labelKey: 'ar.routeTitles.eclAssessments',
        to: '/ar/ecl/assessments',
        icon: 'shieldCheck',
        permission: AR_PERMISSIONS.eclRead,
      },
      {
        id: 'ar-ecl-movement',
        labelKey: 'ar.routeTitles.eclMovement',
        to: '/ar/reports/ecl-movement',
        icon: 'download',
        permission: AR_PERMISSIONS.reportRead,
      },

      /* ECL settings and credit notes (FT3). Credit notes read on
       * `ar.creditnote.read`, the code their route declares. */
      {
        id: 'ar-ecl-settings',
        labelKey: 'ar.routeTitles.eclSettings',
        to: '/ar/ecl/settings',
        icon: 'settings',
        permission: AR_PERMISSIONS.eclRead,
      },
    ],
  },
  {
    id: 's-2.2.4',
    kind: 'feature',
    fsd: '2.2.4',
    code: 'FIN-F04',
    labelKey: 'nav.fsd.s2_2_4.short',
    fullTitleKey: 'nav.fsd.s2_2_4.full',
    items: [
      {
        id: 'assets-register',
        labelKey: 'assets.routeTitles.register',
        to: '/assets/register',
        icon: 'building',
        permission: ASSETS_PERMISSIONS.assetRead,
        descriptionKey: 'nav.items.assetsRegister.description',
      },
      {
        id: 'assets-capitalize',
        labelKey: 'assets.routeTitles.capitalize',
        to: '/assets/register/capitalize',
        icon: 'inbox',
        permission: ASSETS_PERMISSIONS.assetCreate,
      },
      {
        id: 'assets-classes',
        labelKey: 'assets.routeTitles.classes',
        to: '/assets/classes',
        icon: 'settings',
        permission: ASSETS_PERMISSIONS.classRead,
        descriptionKey: 'nav.items.assetsClasses.description',
      },
      // C3 (design §5.9, §13 P9): parallel books, impairment, physical
      // verification. labelKey reuses assets.routeTitles.*.
      {
        id: 'assets-books',
        labelKey: 'assets.routeTitles.books',
        to: '/assets/books',
        icon: 'building',
        permission: ASSETS_PERMISSIONS.bookRead,
      },
      {
        id: 'assets-depreciation',
        labelKey: 'assets.routeTitles.depreciation',
        to: '/assets/depreciation',
        icon: 'refresh',
        permission: ASSETS_PERMISSIONS.depreciationRun,
        descriptionKey: 'nav.items.assetsDepreciation.description',
      },
      {
        id: 'assets-depreciation-preview',
        labelKey: 'assets.routeTitles.depreciationPreview',
        to: '/assets/depreciation/preview',
        icon: 'scale',
        permission: ASSETS_PERMISSIONS.depreciationRun,
      },
      {
        id: 'assets-depreciation-forecast',
        labelKey: 'assets.routeTitles.depreciationForecast',
        to: '/assets/reports/depreciation-forecast',
        icon: 'dashboard',
        permission: ASSETS_PERMISSIONS.reportRead,
      },
      {
        id: 'assets-movement',
        labelKey: 'assets.routeTitles.movement',
        to: '/assets/reports/movement',
        icon: 'download',
        permission: ASSETS_PERMISSIONS.reportRead,
        descriptionKey: 'nav.items.assetsMovement.description',
      },
      {
        id: 'assets-impairments',
        labelKey: 'assets.routeTitles.impairments',
        to: '/assets/impairments',
        icon: 'alert',
        permission: ASSETS_PERMISSIONS.impairmentAssess,
      },
      // FT13-U20 (FSD 2.2.4): disposals with their gain/loss analysis, the per-legal-entity asset
      // settings, inter-company transfers, the depreciation preview and the two FIN-F04 reports.
      // Each permission is the one its route and its read endpoint declare.
      {
        id: 'assets-disposals',
        labelKey: 'assets.routeTitles.disposals',
        to: '/assets/disposals',
        icon: 'history',
        permission: ASSETS_PERMISSIONS.assetRead,
      },
      {
        id: 'assets-verifications',
        labelKey: 'assets.routeTitles.verifications',
        to: '/assets/verifications',
        icon: 'check',
        permission: ASSETS_PERMISSIONS.verificationRead,
      },
      {
        id: 'assets-book-comparison',
        labelKey: 'assets.routeTitles.bookComparison',
        to: '/assets/reports/book-comparison',
        icon: 'scale',
        permission: ASSETS_PERMISSIONS.reportRead,
      },
      {
        id: 'assets-capex-vs-budget',
        labelKey: 'assets.routeTitles.capexVsBudget',
        to: '/assets/reports/capex-vs-budget',
        icon: 'download',
        permission: ASSETS_PERMISSIONS.reportRead,
      },
      {
        id: 'assets-ic-transfer',
        labelKey: 'assets.routeTitles.icTransfer',
        to: '/assets/ic-transfer',
        icon: 'refresh',
        permission: ASSETS_PERMISSIONS.assetRead,
        code: 'FIN-FR-038',
      },
      {
        id: 'assets-settings',
        labelKey: 'assets.routeTitles.settings',
        to: '/assets/settings',
        icon: 'settings',
        permission: ASSETS_PERMISSIONS.classRead,
      },
    ],
  },
  {
    id: 's-2.2.5',
    kind: 'feature',
    fsd: '2.2.5',
    code: 'FIN-F06',
    labelKey: 'nav.fsd.s2_2_5.short',
    fullTitleKey: 'nav.fsd.s2_2_5.full',
    items: [
      /* FT7-U22: a budget model is now a Budget Cycle. The item keeps its id (favourites and
       * recents key off it) and points at the cycles list; `/budget/models` redirects there
       * (`router/legacyRedirects.ts`). */
      {
        id: 'budget-models',
        labelKey: 'budget.routeTitles.budgetCycles',
        to: '/budget/cycles',
        icon: 'dashboard',
        permission: BUDGET_PERMISSIONS.modelRead,
        descriptionKey: 'nav.items.budgetModels.description',
      },
      /* The cross-model version register. Version Comparison has its own item below
       * (`budget-compare`); `itemForPath` prefers the longest matching path, so
       * `/budget/versions/compare` highlights that item, not this one. */
      {
        id: 'budget-versions',
        labelKey: 'budget.routeTitles.budgetVersions',
        to: '/budget/versions',
        icon: 'sitemap',
        permission: BUDGET_PERMISSIONS.versionRead,
      },
      {
        id: 'budget-compare',
        labelKey: 'budget.routeTitles.budgetCompare',
        to: '/budget/versions/compare',
        icon: 'scale',
        permission: BUDGET_PERMISSIONS.versionRead,
      },
      {
        id: 'budget-variance',
        labelKey: 'budget.routeTitles.budgetVariance',
        to: '/budget/variance',
        icon: 'history',
        permission: BUDGET_PERMISSIONS.varianceRead,
        descriptionKey: 'nav.items.budgetVariance.description',
      },
      {
        id: 'budget-availability',
        labelKey: 'budget.routeTitles.budgetAvailability',
        to: '/budget/availability',
        icon: 'scale',
        permission: BUDGET_CYCLE_PERMISSIONS.check,
      },
      /* FT15-U23 (FSD 2.2.5 V05, V07): moving budget between lines, and asking for more of it. The
       * request forms and the detail screens sit under these paths; the decisions are made in the
       * Approvals Inbox. */
      {
        id: 'budget-transfers',
        labelKey: 'budget.routeTitles.budgetTransfers',
        to: '/budget/transfers',
        icon: 'refresh',
        permission: BUDGET_TRANSFER_PERMISSIONS.read,
        code: 'FIN-F06-V05',
      },
      {
        id: 'budget-supplementary',
        labelKey: 'budget.routeTitles.budgetSupplementary',
        to: '/budget/supplementary',
        icon: 'inbox',
        permission: BUDGET_SUPPLEMENTARY_PERMISSIONS.read,
        code: 'FIN-F06-V07',
      },
      /* FT18-U24 (FSD 2.2.5, Revise Forecast): the revised-forecast register - versions that
       * leave the approved budget untouched, with their baselines and revision history. */
      {
        id: 'budget-forecasts',
        labelKey: 'budget.nav.forecasts',
        to: '/budget/forecasts',
        icon: 'trendingUp',
        permission: BUDGET_PERMISSIONS.versionRead,
        descriptionKey: 'budget.forecastRegister.description',
      },
      /* FT13-UB (FSD 2.2.5): a revised forecast against the budget in force, and the materiality
       * threshold (V04) that decides which year-on-year movements need a justification. Both
       * routes are already in BUDGET_ROUTES. */
      {
        id: 'budget-forecast-vs-budget',
        labelKey: 'budget.routeTitles.budgetForecastVsBudget',
        to: '/budget/forecast-vs-budget',
        icon: 'dashboard',
        permission: BUDGET_PERMISSIONS.varianceRead,
      },
      {
        id: 'budget-commitments',
        labelKey: 'budget.routeTitles.budgetCommitments',
        to: '/budget/commitments',
        icon: 'clock',
        permission: BUDGET_PERMISSIONS.commitmentRead,
        descriptionKey: 'nav.items.budgetCommitments.description',
      },
      /* FT11-UC (FSD 2.2.5 F06 V06). The per-cost-centre control level and tolerance, with the
       * legal entity default they inherit. It replaced the company-level budget policy
       * (`budget-policy`, `/budget/policy`), which redirects here (`legacyRedirects.ts`). */
      {
        id: 'budget-control-levels',
        labelKey: 'budget.routeTitles.budgetControlLevels',
        to: '/budget/control-levels',
        icon: 'shield',
        permission: BUDGET_CONTROL_LEVEL_PERMISSIONS.read,
        code: 'FIN-F06-V06',
        descriptionKey: 'nav.items.budgetControlLevels.description',
      },
      {
        id: 'budget-settings',
        labelKey: 'budget.routeTitles.budgetSettings',
        to: '/budget/settings',
        icon: 'settings',
        permission: BUDGET_PERMISSIONS.versionRead,
        code: 'FIN-F06-V04',
      },
    ],
  },
  {
    id: 's-2.2.6',
    kind: 'feature',
    fsd: '2.2.6',
    code: 'FIN-F08',
    labelKey: 'nav.fsd.s2_2_6.short',
    fullTitleKey: 'nav.fsd.s2_2_6.full',
    items: [
      {
        id: 'cost-pools',
        labelKey: 'cost.routeTitles.pools',
        to: '/cost/pools',
        icon: 'building',
        permission: COST_PERMISSIONS.poolRead,
      },
      {
        id: 'cost-drivers',
        labelKey: 'cost.routeTitles.drivers',
        to: '/cost/drivers',
        icon: 'sitemap',
        permission: COST_PERMISSIONS.driverRead,
      },
      {
        id: 'cost-driver-quantities',
        labelKey: 'cost.routeTitles.quantities',
        to: '/cost/quantities',
        icon: 'scale',
        permission: COST_PERMISSIONS.driverRead,
      },
      {
        id: 'cost-cycles',
        labelKey: 'cost.routeTitles.cycles',
        to: '/cost/cycles',
        icon: 'refresh',
        permission: COST_PERMISSIONS.cycleRead,
      },
      {
        id: 'cost-runs',
        labelKey: 'cost.routeTitles.runs',
        to: '/cost/runs',
        icon: 'history',
        permission: COST_PERMISSIONS.runCalculate,
      },
      {
        id: 'cost-absorption-rates',
        labelKey: 'cost.routeTitles.rates',
        to: '/cost/absorption-rates',
        icon: 'scale',
        permission: COST_PERMISSIONS.reportRead,
      },
      {
        id: 'cost-absorption-variance',
        labelKey: 'cost.routeTitles.absorptionVariance',
        to: '/cost/reports/absorption-variance',
        icon: 'dashboard',
        permission: COST_PERMISSIONS.reportRead,
      },
      {
        id: 'cost-profitability',
        labelKey: 'cost.routeTitles.profitability',
        to: '/cost/reports/profitability',
        icon: 'dashboard',
        permission: COST_PERMISSIONS.reportRead,
      },
    ],
  },
  // Consolidation (§8.16). Groups before runs, because a run names a group and
  // cannot exist before one does. A run's five sub-views (trial balance,
  // income statement, balance sheet, eliminations, mismatches) are absent by
  // design: they belong to one run and are reached from it.
  {
    id: 's-2.2.7',
    kind: 'feature',
    fsd: '2.2.7',
    code: 'FIN-F07',
    labelKey: 'nav.fsd.s2_2_7.short',
    fullTitleKey: 'nav.fsd.s2_2_7.full',
    items: [
      {
        id: 'consol-groups',
        labelKey: 'consol.routeTitles.groups',
        to: '/consol/groups',
        icon: 'building',
        permission: CONSOL_PERMISSIONS.groupRead,
        keywordsKey: 'nav.items.consolGroups.keywords',
        descriptionKey: 'nav.items.consolGroups.description',
      },
      {
        id: 'consol-account-maps',
        labelKey: 'consol.routeTitles.accountMaps',
        to: '/consol/account-maps',
        icon: 'sitemap',
        permission: CONSOL_PERMISSIONS.mappingRead,
        code: 'FIN-F07-V02',
      },
      /* FT11-UC (FSD 2.2.7 V01, V02). Whether each member is ready to be consolidated for a
       * period, and the local-to-group account map that gives every balance a group account. */
      {
        id: 'consol-readiness',
        labelKey: 'consol.routeTitles.readiness',
        to: '/consol/readiness',
        icon: 'shieldCheck',
        permission: CONSOL_PERMISSIONS.readinessRead,
        code: 'FIN-F07-V01',
      },
      {
        id: 'consol-runs',
        labelKey: 'consol.routeTitles.runs',
        to: '/consol/runs',
        icon: 'refresh',
        permission: CONSOL_PERMISSIONS.runRead,
        keywordsKey: 'nav.items.consolRuns.keywords',
        descriptionKey: 'nav.items.consolRuns.description',
      },
      /* FT14-U26 (FSD 2.2.7 V06): group adjustments, prepared on a run and approved by the Group
       * Controller step in the Approvals Inbox. The form and detail routes sit under this path; a
       * run's statement pack and analyses sit under `consol-runs`. */
      {
        id: 'consol-adjustments',
        labelKey: 'consol.routeTitles.adjustments',
        to: '/consol/adjustments',
        icon: 'scale',
        permission: CONSOL_ADJUSTMENT_PERMISSIONS.read,
        code: 'FIN-F07-V06',
      },
    ],
  },
  {
    id: 's-2.2.8',
    kind: 'feature',
    fsd: '2.2.8',
    code: 'FIN-F09',
    labelKey: 'nav.fsd.s2_2_8.short',
    fullTitleKey: 'nav.fsd.s2_2_8.full',
    items: [
      {
        id: 'close-board',
        labelKey: 'close.routeTitles.board',
        to: '/close/board',
        icon: 'dashboard',
        permission: CLOSE_PERMISSIONS.runRead,
      },
      {
        id: 'close-readiness',
        labelKey: 'close.routeTitles.readiness',
        to: '/close/readiness',
        icon: 'shieldCheck',
        permission: CLOSE_PERMISSIONS.runRead,
      },
      {
        id: 'close-runs',
        labelKey: 'close.routeTitles.runs',
        to: '/close/runs',
        icon: 'history',
        permission: CLOSE_PERMISSIONS.runRead,
      },
      {
        id: 'close-calendars',
        labelKey: 'close.routeTitles.calendars',
        to: '/close/calendars',
        icon: 'calendar',
        permission: CLOSE_PERMISSIONS.calendarRead,
      },
      // FT13-U28 (FSD 2.2.8): the certification register, sub-ledger to GL reconciliation, evidence
      // packs, cycle analysis and the financial statement sets. The statement-set detail
      // (`/close/statements/:id`, the published-notification target) resolves under its list item.
      {
        id: 'close-certifications',
        labelKey: 'close.ft13.routeTitles.certifications',
        to: '/close/certifications',
        icon: 'shieldCheck',
        permission: CLOSE_PERMISSIONS.certificationRead,
      },
      {
        id: 'close-reconciliation',
        labelKey: 'close.ft13.routeTitles.reconciliation',
        to: '/close/reconciliation',
        icon: 'scale',
        permission: CLOSE_PERMISSIONS.reconciliationRead,
        code: 'FIN-F09-V02',
      },
      {
        id: 'close-statements',
        labelKey: 'close.ft13.routeTitles.statements',
        to: '/close/statements',
        icon: 'dashboard',
        permission: CLOSE_PERMISSIONS.statementsRead,
      },
      {
        id: 'close-evidence-packs',
        labelKey: 'close.ft13.routeTitles.evidencePacks',
        to: '/close/evidence-packs',
        icon: 'inbox',
        permission: CLOSE_PERMISSIONS.evidenceRead,
      },
      {
        id: 'close-cycle-analysis',
        labelKey: 'close.ft13.routeTitles.cycleAnalysis',
        to: '/close/cycle-analysis',
        icon: 'clock',
        permission: CLOSE_PERMISSIONS.runRead,
      },
      /* FT18-U29 (FSD 2.2.8): the anomaly review - the findings the close scan raised, with the
       * disposition each needs before the period can close. */
      {
        id: 'close-anomalies',
        labelKey: 'close.anomalyReview.nav',
        to: '/close/anomalies',
        icon: 'alert',
        permission: CLOSE_PERMISSIONS.anomalyRead,
        descriptionKey: 'close.anomalyReview.description',
      },
      // R0 W1 (FSD 2.2.8, 2.2.1 year-end): read-only plan of the year-end close.
      // The route declares `gl.report.read`, the permission the server checks.
      {
        id: 'close-year-end-plan',
        labelKey: 'nav.items.closeYearEndPlan.label',
        to: '/close/year-end-plan',
        icon: 'calendar',
        permission: GL_PERMISSIONS.reportRead,
        keywordsKey: 'nav.items.closeYearEndPlan.keywords',
        descriptionKey: 'nav.items.closeYearEndPlan.description',
      },
    ],
  },
  // Part 2 requirement catalogue (2.1). Screens answering a catalogue
  // requirement that no feature specification covers; `code` names it.
  {
    id: 's-2.1',
    kind: 'catalogue',
    fsd: '2.1',
    labelKey: 'nav.fsd.s2_1.short',
    fullTitleKey: 'nav.fsd.s2_1.full',
    items: [
      /* Tax (FIN-FR-019, FIN-FR-020). Every permission is the *read* half of the
       * pair `internal/tax` registers; filing a return (`tax.return.file`)
       * carries step-up MFA and is gated on the screen's own action, never on
       * the door. Detail routes — one return's boxes — are reached from their
       * list. */
      {
        // C0's determination simulator.
        id: 'tax-simulator',
        labelKey: 'nav.items.taxSimulator.label',
        to: '/tax/simulator',
        icon: 'settings',
        permission: PERMISSIONS.taxCodeRead,
        keywordsKey: 'nav.items.taxSimulator.keywords',
        descriptionKey: 'nav.items.taxSimulator.description',
        code: 'FIN-FR-019',
      },
      {
        id: 'tax-report',
        labelKey: 'nav.items.taxReport.label',
        to: '/tax/report',
        icon: 'dashboard',
        permission: PERMISSIONS.taxReportRead,
        keywordsKey: 'nav.items.taxReport.keywords',
        descriptionKey: 'nav.items.taxReport.description',
        code: 'FIN-FR-019',
      },
      {
        id: 'tax-registrations',
        labelKey: 'tax.spine.routeTitles.registrations',
        to: '/tax/registrations',
        icon: 'building',
        permission: TAX_SPINE_PERMISSIONS.registrationRead,
        code: 'FIN-FR-019',
      },
      {
        id: 'tax-certificates',
        labelKey: 'tax.spine.routeTitles.certificates',
        to: '/tax/exemption-certificates',
        icon: 'shieldCheck',
        permission: TAX_SPINE_PERMISSIONS.certificateRead,
        code: 'FIN-FR-019',
      },
      {
        id: 'tax-charges',
        labelKey: 'tax.spine.routeTitles.charges',
        to: '/tax/charges',
        icon: 'scale',
        permission: TAX_SPINE_PERMISSIONS.chargeRead,
        code: 'FIN-FR-019',
      },
      {
        id: 'tax-returns',
        labelKey: 'tax.spine.routeTitles.returns',
        to: '/tax/returns',
        icon: 'calendar',
        permission: TAX_SPINE_PERMISSIONS.returnRead,
        code: 'FIN-FR-020',
      },
      // Expense management (FIN-FR-026 claims, FIN-FR-027 travel and advances,
      // FIN-FR-028 corporate cards).
      {
        id: 'expense-claims',
        labelKey: 'expense.routeTitles.claims',
        to: '/expense/claims',
        icon: 'inbox',
        permission: EXPENSE_PERMISSIONS.claimRead,
        code: 'FIN-FR-026',
      },
      {
        id: 'expense-approvals',
        labelKey: 'expense.routeTitles.approvals',
        to: '/expense/approvals',
        icon: 'shieldCheck',
        permission: EXPENSE_PERMISSIONS.claimApprove,
        code: 'FIN-FR-026',
      },
      {
        id: 'expense-policies',
        labelKey: 'expense.routeTitles.policies',
        to: '/expense/policies',
        icon: 'settings',
        permission: EXPENSE_PERMISSIONS.policyRead,
        code: 'FIN-FR-026',
      },
      {
        id: 'expense-travel',
        labelKey: 'expense.routeTitles.travel',
        to: '/expense/travel',
        icon: 'mapPin',
        permission: EXPENSE_PERMISSIONS.travelRead,
        code: 'FIN-FR-027',
      },
      {
        id: 'expense-advances',
        labelKey: 'expense.routeTitles.advances',
        to: '/expense/advances',
        icon: 'clock',
        permission: EXPENSE_PERMISSIONS.travelRead,
        code: 'FIN-FR-027',
      },
      {
        id: 'expense-cards',
        labelKey: 'expense.routeTitles.cards',
        to: '/expense/cards',
        icon: 'key',
        permission: EXPENSE_PERMISSIONS.cardRead,
        code: 'FIN-FR-028',
      },
      {
        id: 'expense-card-accounts',
        labelKey: 'expense.routeTitles.cardAccounts',
        to: '/expense/card-accounts',
        icon: 'building',
        permission: EXPENSE_PERMISSIONS.cardRead,
        code: 'FIN-FR-028',
      },
      /* FT15-U (FIN-FR-026/027): the payroll inclusions ledger - approved claims routed to payroll
       * and advances recovered from pay, with the payroll run each one rode on. */
      {
        id: 'expense-payroll-inclusions',
        labelKey: 'expense.nav.payrollInclusions',
        to: '/expense/payroll-inclusions',
        icon: 'users',
        permission: EXPENSE_PERMISSIONS.claimSettlePayroll,
        code: 'FIN-FR-026',
      },
      {
        id: 'expense-settings',
        labelKey: 'expense.routeTitles.settings',
        to: '/expense/settings',
        icon: 'settings',
        permission: EXPENSE_PERMISSIONS.policyRead,
        code: 'FIN-FR-026',
      },
      // Intercompany reconciliation (FIN-FR-030): the reconciliation pair
      // register and the matching, statements and ageing worked against it.
      // R0 moved `interco-pairs` back here from Part 1 — these are Finance
      // reconciliation pairs, not FSD 1.2.3 legal-entity intercompany rules
      // (those are `enterprise-intercompany`, in `p01-enterprise.ts`).
      {
        id: 'interco-pairs',
        labelKey: 'interco.routeTitles.pairs',
        to: '/interco/pairs',
        icon: 'link',
        permission: INTERCO_PERMISSIONS.pairRead,
        code: 'FIN-FR-030',
      },
      {
        id: 'interco-match',
        labelKey: 'interco.routeTitles.match',
        to: '/interco/match',
        icon: 'check',
        permission: INTERCO_PERMISSIONS.pairRead,
        code: 'FIN-FR-030',
      },
      {
        id: 'interco-statements',
        labelKey: 'interco.routeTitles.statements',
        to: '/interco/statements',
        icon: 'history',
        permission: INTERCO_PERMISSIONS.pairRead,
        code: 'FIN-FR-030',
      },
      {
        id: 'interco-ageing',
        labelKey: 'interco.routeTitles.ageing',
        to: '/interco/ageing',
        icon: 'clock',
        permission: INTERCO_PERMISSIONS.pairRead,
        code: 'FIN-FR-030',
      },
      /* FT19 (FIN-FR-021, FIN-FR-023, FIN-FR-034): lease contracts with their schedules and runs,
       * foreign-currency exposure with period revaluation, and the single enterprise revenue
       * policy. The detail and run routes sit under their list path or are aliased
       * (`nav/aliases.ts`). */
      {
        id: 'leases',
        labelKey: 'lease.routeTitles.list',
        to: '/finance/leases',
        icon: 'building',
        permission: LEASE_PERMISSIONS.read,
        code: 'FIN-FR-021',
        descriptionKey: 'lease.list.description',
      },
      {
        id: 'fx-exposure',
        labelKey: 'fx.exposure.navLabel',
        to: '/finance/fx-exposure',
        icon: 'trendingUp',
        permission: FX_EXPOSURE_PERMISSIONS.exposureRead,
        code: 'FIN-FR-023',
        descriptionKey: 'fx.exposure.navDescription',
      },
      /* FT20-U34 (FIN-FR-024): customer liability instruments - gift cards, store credit,
       * deposits and on-account receipts - with the breakage report and policy as tabs. */
      {
        id: 'liability-instruments',
        labelKey: 'liability.nav.label',
        to: '/finance/liability-instruments',
        icon: 'wallet',
        permission: LIABILITY_PERMISSIONS.read,
        code: 'FIN-FR-024',
        descriptionKey: 'liability.nav.description',
      },
      /* FT19-U33 (FIN-FR-031): the approved transfer pricing method by pair and category, a
       * price quote, and the departures report. */
      {
        id: 'interco-transfer-pricing',
        labelKey: 'interco.tp.navLabel',
        to: '/finance/interco/transfer-pricing',
        icon: 'scale',
        permission: TP_PERMISSIONS.read,
        code: 'FIN-FR-031',
        descriptionKey: 'interco.tp.navDescription',
      },
      {
        id: 'revenue-policy',
        labelKey: 'revpolicy.nav.label',
        to: '/finance/revenue-policy',
        icon: 'shieldCheck',
        permission: REVPOLICY_PERMISSIONS.read,
        code: 'FIN-FR-034',
        descriptionKey: 'revpolicy.nav.description',
      },
    ],
  },
]
